import { mapNegativeNumberToZero } from '../../../graphql/schema/queries/value-reporting/map-negative-number-to-zero'
import { Connector } from '../../connector'

export interface VrVulnerabilitiesQueryData {
  readonly valueReportingVulnerabilities: VrVulnerabilitiesResolverResponse
}

export interface SelectedValueReportingFacet {
  readonly facetName: string
  readonly facetValue: string
}

export interface VrAggregationQueryArgs {
  readonly organizationUuid: string
  readonly selectedFacets?: SelectedValueReportingFacet[]
}

/** This is the type of facet that gets returned from the API for value reporting application facets */
export interface VrApplicationFacet {
  readonly name: string
  readonly displayName: string
  readonly totalCount: number
  readonly values: Array<{
    readonly count: number
    readonly value: string
    readonly displayName: string
  }>
}

export interface VrVulnerabilitiesFacetValue {
  readonly count: number
  readonly displayName: string
  readonly value: string
}

export interface VrVulnerabilitiesResolverResponse {
  readonly totalVulnerabilities: number
  readonly facets: {
    readonly vulnerabilitySeverity: VrVulnerabilitiesFacetValue[]
    readonly vulnerabilityStatus: VrVulnerabilitiesFacetValue[]
    readonly vulnerabilityType: VrVulnerabilitiesFacetValue[]
  }
}

export interface VrVulnerabilitiesQueryData {
  readonly valueReportingVulnerabilities: VrVulnerabilitiesResolverResponse
}

export interface VrVulnerabilitiesQueryVariables {
  readonly organizationUuid: string
  readonly selectedFacets?: SelectedValueReportingFacet[]
}

const validFacets = [
  'vulnerabilitySeverity',
  'vulnerabilityStatus',
  'vulnerabilityType'
]

export function normalizeFacets(
  facets: VrApplicationFacet[]
): VrVulnerabilitiesResolverResponse['facets'] {
  return facets.reduce<VrVulnerabilitiesResolverResponse['facets']>(
    (prev, { name, values }) => {
      const normalizedName = name.replace(/-./g, (str) => str.toUpperCase()[1])

      if (validFacets.includes(normalizedName)) {
        return {
          ...prev,
          [normalizedName]: values.map(({ count, value, displayName }) => {
            return {
              count: mapNegativeNumberToZero(count),
              value,
              displayName
            }
          })
        }
      }

      return prev
    },
    {
      vulnerabilityStatus: [],
      vulnerabilitySeverity: [],
      vulnerabilityType: []
    }
  )
}

export async function getVrVulnerabilities(
  this: { readonly connector: Connector },
  { organizationUuid, selectedFacets = [] }: VrAggregationQueryArgs
) {
  const { facets } = await this.connector.post(
    `/organizations/${organizationUuid}/application-security-summary/vulnerabilities`,
    { facets: selectedFacets }
  )

  return {
    totalVulnerabilities:
      facets.length > 0 ? mapNegativeNumberToZero(facets[0].totalCount) : 0,
    facets: normalizeFacets(facets)
  }
}
